import React from 'react';
import { Plus, Download, RefreshCw, Trash2 } from 'lucide-react';
import authService from '../../../services/authService';
import useMobileHeaderActions from '../../../hooks/useMobileHeaderActions';
import TechnicianFilter from '../../TechnicianFilter';
import MobileFAB from '../../MobileFAB';

const CustomerHeader = ({
  isQbConnected,
  isImporting,
  isSSEImporting,
  sseProgress,
  importProgress,
  customers,
  customerCount,
  userProfile,
  onAddCustomer,
  onAddJob,
  onAddContract,
  onImportFromQuickBooks,
  onDeleteAllCustomers,
  techFilter
}) => {
  const isAdminSession = userProfile?.role === 'admin' || authService.hasAdminReturn();

  useMobileHeaderActions(
    <>
      {techFilter?.showFilter && <TechnicianFilter {...techFilter} compact />}
      {isQbConnected && isAdminSession && (
        <button
          onClick={onImportFromQuickBooks}
          disabled={isImporting || isSSEImporting}
          className="btn btn-sm btn-secondary"
        >
          <img src="/quickbooks-logo.svg" alt="QB" className="w-4 h-4" />
          {(isImporting || isSSEImporting) ? (
            <RefreshCw size={14} className="animate-spin" />
          ) : (
            <Download size={14} />
          )}
          <span>
            {(isImporting || isSSEImporting) ? 'Importing...' : 'Import'}
          </span>
        </button>
      )}
    </>,
    [onImportFromQuickBooks, isImporting, isSSEImporting, isQbConnected, isAdminSession, techFilter?.showFilter, techFilter?.selectedTechnician, techFilter?.techGroups]
  );

  return (
    <div className="mb-2 md:mb-6">
      <MobileFAB onAddCustomer={onAddCustomer} onAddJob={onAddJob} onAddContract={onAddContract} />
      {/* Desktop Layout */}
      <div className="hidden md:flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Customer List</h1>
        </div>
        <div className="flex items-center space-x-3">
        {isQbConnected && isAdminSession && (
          <button
            onClick={onImportFromQuickBooks}
            disabled={isImporting || isSSEImporting}
            className="btn btn-md btn-secondary"
          >
            <img src="/quickbooks-logo.svg" alt="QuickBooks" className="w-4 h-4" />
            {(isImporting || isSSEImporting) ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            <span>
              {(isImporting || isSSEImporting) ? (
                sseProgress.total > 0 || importProgress.total > 0 ?
                  (() => {
                    const current = sseProgress.total > 0 ? sseProgress.current : importProgress.current;
                    const total = sseProgress.total > 0 ? sseProgress.total : importProgress.total;
                    const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
                    return `Importing ${current}/${total} - ${percentage}% complete`;
                  })() :
                  'Importing from QuickBooks...'
              ) : (
                'Import from QuickBooks'
              )}
            </span>
          </button>
        )}

        {/* Admin-only Delete All Customers Button - Only visible when admin is impersonating franchisee */}
        {authService.hasAdminReturn() && userProfile?.role !== 'admin' && customers.length > 0 && (
          <button
            onClick={onDeleteAllCustomers}
            className="btn btn-md btn-danger"
            title="Admin only: Delete all local customers (for testing)"
          >
            <Trash2 size={16} />
            <span>Delete All ({customerCount || customers.length})</span>
          </button>
        )}

        <button
          onClick={onAddCustomer}
          className="btn btn-md btn-secondary"
        >
          <Plus size={16} />
          <span>Add Customer</span>
        </button>
        {onAddJob && (
          <button onClick={onAddJob} className="btn btn-md btn-secondary">
            <Plus size={16} />
            <span>Add Job</span>
          </button>
        )}
        {onAddContract && (
          <button onClick={onAddContract} className="btn btn-md btn-secondary">
            <Plus size={16} />
            <span>Add Contract</span>
          </button>
        )}
        {techFilter && <TechnicianFilter {...techFilter} />}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden mb-2">
        {authService.hasAdminReturn() && userProfile?.role !== 'admin' && customers.length > 0 && (
          <button
            onClick={onDeleteAllCustomers}
            className="btn btn-sm btn-danger w-full mt-2"
            title="Admin only: Delete all local customers (for testing)"
          >
            <Trash2 size={14} />
            <span className="text-xs font-bold">Delete All ({customerCount || customers.length})</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomerHeader;
